import { z } from 'zod';

/**
 * The composer, and only the composer. The conversation itself is component
 * state rather than form data - it is appended to, never edited, so there is
 * nothing for a form to own.
 *
 * `nullish` because the field starts empty: the form is initialised with
 * `chat: null` and reset back to it after every send.
 */
export const ChatFormSchema = z.object({
  chat: z
    .string()
    /*
      Whitespace is not a prompt. `Form` runs this as Formik's `validate`, so a
      failure here is what actually stops the send - which matters most for the
      Enter key, since that reaches `fireSubmit` without passing the send button's
      disabled state.

      Written as "nothing typed, or something that isn't only spaces" rather than
      the `.trim().min(1)` used elsewhere in the repo, because an empty composer is
      this field's resting state, not a mistake. `RawTextarea` reports an emptied
      editor as `''` rather than null, and `FormField` shows a field's error once it
      has been touched - so `.min(1)` would put a validation message under the
      composer every time someone typed and then deleted it.

      The check is on the raw value for the same reason `.trim()` is not chained
      ahead of it: trimming first turns "   " into "" and the emptiness clause would
      then wave it through.
    */
    .refine(value => value.length === 0 || value.trim().length > 0, {
      message: 'Enter a prompt',
    })
    .nullish(),
});

export type ChatForm = z.infer<typeof ChatFormSchema>;
